import React, { useEffect, useMemo, useState } from "react";
import "../HotelDetailsBox/HotelDetailsBox.scss";
import hotelNoImg from "../../assets/images/hotels/no_img.png";
import { Card, Col, Rate, Row, Modal } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { formatDate, DISPLAY_FORMAT, formatHotelPortalCancellationDisplayDate, applyHotelPortalCancellationDisplayOffset, getStayNights, toStayDateOnly } from "../../helpers/dateHelper";
import {
  buildHotelCancellationNarrativeFromSlabs,
  resolveHotelCancellationNarrative,
  buildHotelNonRefundableCheckoutCancellationText,
  getHotelNonRefundableCheckoutDisplayLines,
  isHotelRatePlanNonRefundableForCheckout,
  resolveHotelApiCancellationDeadline,
} from "../../pages/Hotels/hotelCancellation";
import { getRoomMealCategoryLabel } from "../../pages/Hotels/hotelMealTypes";
import ImagesLightbox from "../ImagesLightbox/ImagesLightbox";
import queryString from "query-string";
import { useNavigate } from "react-router-dom";
import { getHotelRoomDisplayTitle } from "../../pages/Hotels/hotelRoomDisplay";

dayjs.extend(customParseFormat);

const CAROUSEL_MAX = 20;

const collectImageUrls = (...sources) => {
  const urls = [];
  const seen = new Set();
  for (const source of sources) {
    if (!Array.isArray(source)) continue;
    for (const item of source) {
      const url = String(
        typeof item === "string"
          ? item
          : item?.url ?? item?.Url ?? item?.tns ?? item?.path ?? "",
      ).trim();
      if (!url || url.length < 8 || seen.has(url)) continue;
      if (/no_img|placeholder|HotelNA\.jpg|no_photo/i.test(url)) continue;
      seen.add(url);
      urls.push(url);
    }
  }
  return urls;
};

const HotelDetailsBox = ({ Ids, hotelDetailsObj, hotelSearchData }) => {
  let navigate = useNavigate();
  const safeHotel = hotelDetailsObj ?? {};
  const safeSearch = hotelSearchData ?? {};
  const rooms = Array.isArray(safeHotel.rooms) ? safeHotel.rooms : [];
  const firstRoom = rooms[0] ?? null;
  const firstRatePlan =
    firstRoom?.ratePlans?.[0] ?? firstRoom?.RatePlans?.[0] ?? null;
  const roomMealCategory = getRoomMealCategoryLabel(firstRoom, firstRatePlan);
  const roomGuests = Array.isArray(safeSearch.roomGuests) ? safeSearch.roomGuests : [];
  const imageList = Array.isArray(safeHotel.imageList) ? safeHotel.imageList : [];
  const hotelImages = useMemo(
    () =>
      collectImageUrls(
        safeHotel.images,
        safeHotel.Images,
        safeHotel.imageList,
        safeHotel.ImageList,
      ),
    [safeHotel],
  );
  const carouselImages = useMemo(
    () => hotelImages.slice(0, CAROUSEL_MAX),
    [hotelImages],
  );
  const [imageIndex, setImageIndex] = useState(0);
  const [imagesModal, setImagesModal] = useState({
    visible: false,
    startIndex: 0,
  });

  useEffect(() => {
    setImageIndex(0);
  }, [carouselImages.length, safeHotel?.hotelCode, safeHotel?.hotelName]);

  const goPrev = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (carouselImages.length <= 1) return;
    setImageIndex((i) => (i - 1 + carouselImages.length) % carouselImages.length);
  };

  const goNext = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (carouselImages.length <= 1) return;
    setImageIndex((i) => (i + 1) % carouselImages.length);
  };

  const openGallery = (startIndex = imageIndex) => {
    setImagesModal({ visible: true, startIndex });
  };
  const cancellationPolicies = Array.isArray(firstRatePlan?.cancellationPolicy)
    ? firstRatePlan.cancellationPolicy
    : [];
  const isSyntheticPolicy = (policy) => {
    if (!policy) return true;
    const name = String(policy?.policyName ?? "").toLowerCase();
    const penalty = String(policy?.penaltyAmount ?? "");
    const from = String(policy?.fromDate ?? "");
    const to = String(policy?.toDate ?? "");
    return (
      name === "non-refundable" ||
      (penalty === "100" && from && to && from === to)
    );
  };

  const supplierCancellationText = (() => {
    for (const room of rooms) {
      for (const rp of room?.ratePlans ?? []) {
        if (isHotelRatePlanNonRefundableForCheckout(rp)) {
          const nrText = buildHotelNonRefundableCheckoutCancellationText(rp);
          if (nrText) return nrText;
          continue;
        }
        if (rp?.refundable === false || rp?.Refundable === false) {
          const nrText = buildHotelNonRefundableCheckoutCancellationText(rp);
          if (nrText) return nrText;
          continue;
        }
        const fromSlabs = buildHotelCancellationNarrativeFromSlabs(rp);
        if (fromSlabs) return fromSlabs;
        const text = resolveHotelCancellationNarrative(rp);
        if (text) return text;
      }
    }
    if (firstRatePlan?.refundable === false || firstRatePlan?.Refundable === false) {
      return buildHotelNonRefundableCheckoutCancellationText(firstRatePlan);
    }
    return buildHotelCancellationNarrativeFromSlabs(firstRatePlan) ||
      resolveHotelCancellationNarrative(firstRatePlan);
  })();
  const isNonRefundableCheckout =
    firstRatePlan && isHotelRatePlanNonRefundableForCheckout(firstRatePlan);
  const canonicalCutoffRaw = isNonRefundableCheckout
    ? ""
    : resolveHotelApiCancellationDeadline(firstRatePlan) ||
      firstRatePlan?.tripjackCancellationCutoff ||
      firstRatePlan?.lastCancellationDate ||
      firstRatePlan?.lastVoucherDate ||
      cancellationPolicies.find((p) => parseFloat(p?.penaltyAmount) === 0)?.toDate;

  const normalizeNarrativeCutoffDate = (text, cutoffRaw) => {
    const raw = String(text ?? "").trim();
    if (!raw) return "";
    const displayCutoff = applyHotelPortalCancellationDisplayOffset(cutoffRaw);
    if (!displayCutoff?.isValid?.()) return raw;

    const displayIso = displayCutoff.format("YYYY-MM-DD");
    const displayLong = displayCutoff.format("DD MMMM");

    let result = raw;

    // Tripjack scnp: "Cancel for free before 2026-08-28"
    result = result.replace(
      /(cancel for free before\s+)(\d{4}-\d{2}-\d{2})/i,
      `$1${displayIso}`,
    );

    // Slab narrative: "... before 12:00 AM, 26 July - 0%"
    result = result.replace(
      /(cancellations done before 12:00 AM,\s*)(\d{1,2}\s+[A-Za-z]+)(\s*-\s*(?:INR\s*)?0\.?0?%?)/i,
      `$1${displayLong}$3`,
    );

    // Slab narrative: "... after 12:00 AM, 23 September - 45%"
    result = result.replace(
      /(cancellations done after 12:00 AM,\s*)(\d{1,2}\s+[A-Za-z]+)(\s*-\s*[\d.]+%)/gi,
      (match, prefix, datePart, suffix) => {
        const parsed = dayjs(datePart, "D MMMM", true);
        if (!parsed.isValid()) return match;
        const shifted = applyHotelPortalCancellationDisplayOffset(parsed);
        if (!shifted?.isValid?.()) return match;
        return `${prefix}${shifted.format("D MMMM")}${suffix}`;
      },
    );

    return result;
  };

  const nrCheckoutDisplayLines = isNonRefundableCheckout
    ? getHotelNonRefundableCheckoutDisplayLines(firstRatePlan)
    : [];
  const checkoutCancellationText = isNonRefundableCheckout
    ? nrCheckoutDisplayLines.join(" ")
    : normalizeNarrativeCutoffDate(
        supplierCancellationText,
        canonicalCutoffRaw,
      );

  const showCancellationDates =
    !isNonRefundableCheckout &&
    Boolean(canonicalCutoffRaw) &&
    dayjs(canonicalCutoffRaw).isAfter(dayjs());

  let noOfNights = () => {
    if (Object.keys(safeSearch).length > 0) {
      const checkinDate =
        safeSearch.checkInDate || safeSearch.checkIn;
      const checkoutDate =
        safeSearch.checkOutDate || safeSearch.checkOut;
      return getStayNights(checkinDate, checkoutDate);
    }
    return 0;
  };

  const displayCheckIn = toStayDateOnly(
    safeSearch.checkInDate || safeSearch.checkIn,
  );
  const displayCheckOut = toStayDateOnly(
    safeSearch.checkOutDate || safeSearch.checkOut,
  );

  const getAdultChildCount = () => {
    let adults = 0;
    let childs = 0;

    if (roomGuests.length > 0) {
      for (let index = 0; index < roomGuests.length; index++) {
        adults += Number(roomGuests[index].noOfAdults);
        childs += Number(roomGuests[index].noOfChilds);
      }
    }
    if (childs > 0) {
      return `${adults} Adult &  ${childs} Children`;
    } else return `${adults} Adult`;
  };

  const safeIndex = Math.min(imageIndex, Math.max(carouselImages.length - 1, 0));

  return (
    <>
      <Row>
        <Col md={8} sm={24} xs={24}>
          <div className="image-details one-img-hotel hotel-checkout-image-carousel">
            {carouselImages.length > 0 ? (
              <>
                <button
                  type="button"
                  className="hotel-image-open"
                  onClick={() => openGallery(safeIndex)}
                  aria-label="Open hotel images"
                >
                  <img
                    src={carouselImages[safeIndex]}
                    alt={safeHotel.hotelName}
                    onError={(e)=>e.target.src=require('../../assets/images/hotelfallbackimg.jpg' )}
                  />
                </button>
                {carouselImages.length > 1 ? (
                  <>
                    <button
                      type="button"
                      className="hotel-carousel-nav hotel-carousel-nav--prev"
                      aria-label="Previous image"
                      onClick={goPrev}
                    >
                      <LeftOutlined />
                    </button>
                    <button
                      type="button"
                      className="hotel-carousel-nav hotel-carousel-nav--next"
                      aria-label="Next image"
                      onClick={goNext}
                    >
                      <RightOutlined />
                    </button>
                    <div className="hotel-carousel-counter">
                      {safeIndex + 1}/{carouselImages.length}
                    </div>
                  </>
                ) : null}
                <div
                  className="gallery-icon"
                  onClick={() => openGallery(safeIndex)}
                  title="View gallery"
                >
                  <i className="fa fa-search-plus" aria-hidden="true" />
                </div>
              </>
            ) : (
              <img src={hotelNoImg} alt="no-photo" />
            )}

            <div className="tag-cp-img">
              <p>You Save Rs.2,640 </p>
            </div>
          </div>
        </Col>

        <Col md={16} sm={24} xs={24}>
          <div className="disp-table-cell-div">
            <div className="hotel-plan-div">
              <h4>{safeHotel.hotelName}</h4>
              {safeHotel.starRating && (
                <div className="hotel-star">
                  <Rate
                    className="starRating"
                    disabled
                    value={Number(safeHotel.starRating)}
                    allowHalf={true}
                    style={{ whiteSpace: 'nowrap' }}
                  />
                </div>
              )}
            </div>
            <p className="post-div-para">
              {Object.hasOwnProperty(safeHotel?.addresses) && safeHotel?.addresses[0]?.address}
            </p>
          </div>
          <Row gutter={[2, 12]} className="cal-top-date-pic">
            <Col md={12} sm={12} xs={12}>
              <div className="cal-block-div">
                <div className="cb-top-div">
                  <span className="checkin-text-div">Check-In</span>
                  <span className="bold bkg-date-div">
                    {formatDate(displayCheckIn || safeSearch.checkInDate || safeSearch.checkIn, "DD, MMM YYYY")}
                  </span>
                </div>
              </div>
            </Col>
            <Col md={12} sm={12} xs={12}>
              <div className="cal-block-div">
                <div className="cb-top-div">
                  <span className="checkin-text-div">Check-Out</span>
                  <span className="bold bkg-date-div">
                    {formatDate(displayCheckOut || safeSearch.checkOutDate || safeSearch.checkOut, "DD, MMM YYYY")}
                  </span>
                </div>
              </div>
            </Col>
          </Row>

          <Row gutter={[2, 12]}>
            <Col md={12} sm={12} xs={12}>
              <Col className="room-number-wrapper" style={{marginBottom:'0px',paddingTop:'4px',marginRight:'8px',backgroundColor:'#f5f5f5'}}>
                <div className="second-col-details-div">
                  <ul className="cp-room-1" style={{paddingLeft:'12px'}}>
                    <li className="room-number-days">
                      {noOfNights() + 1} Days & {noOfNights()} Nights
                    </li>
                  </ul>

                  {roomGuests.length > 0 &&
                    roomGuests.map((item, index) => {
                      return (
                        <>
                          <ul className="spin-days-rooms" style={{justifyContent:'space-evenly'}}>
                            <li className="room-spin-cp" style={{marginRight:'0px'}}>Room {index + 1}:</li>
                            <li className="room-pkg-spin-cp">
                              {item.noOfChilds > 0
                                ? `${item.noOfAdults} Adults & ${item.noOfChilds} Childs`
                                : `${item.noOfAdults} Adults`}
                            </li>
                          </ul>
                        </>
                      );
                    })}
                </div>
              </Col>
            </Col>
            <Col md={12} sm={24} xs={24}>
              <Col className="inclusion-cp" style={{marginRight:'8px',marginTop:'10px',paddingLeft:'8px',backgroundColor:'#f5f5f5'}}>
              <div>
              {(firstRoom?.roomName ?? "").split(",").map((i, idx) =>
                  idx > 0 ? (
                    <>
                      {idx === 1 ? <p>Inclusion:</p> : null}
                    </>
                  ) : (
                    ""
                  )
                )}
                </div>
                <div>
                {(firstRoom?.roomName ?? "").split(",").map((i, idx) =>
                  idx > 0 ? (
                    <>
                      <ul>
                        <li>
                          <i className="fa fa-check" aria-hidden="true"></i>
                          &nbsp;
                          {i}
                        </li>
                      </ul>
                    </>
                  ) : (
                    ""
                  )
                )}
                </div>
              </Col>
            </Col>
          </Row>

        </Col>


        <Col md={24} sm={24} xs={24} className="cancel-cp-bottom">
          <>
            {showCancellationDates && (
              <div
                className="hotelCancellationDate text-success d-flex"
                style={{ justifyContent: "space-between" }}
              >
                <p>
                  <strong>Last Cancellation Date :- </strong>
                  {formatHotelPortalCancellationDisplayDate(
                    canonicalCutoffRaw,
                    "DD-MM-YYYY",
                  )}
                </p>
                <p>
                  <strong>Last Voucher Date :- </strong>
                  {formatHotelPortalCancellationDisplayDate(
                    canonicalCutoffRaw,
                    "DD-MM-YYYY",
                  )}
                </p>
              </div>
            )}
            {isNonRefundableCheckout && (
              <p className="text-danger mb-2"><strong>Non-refundable</strong></p>
            )}
            {rooms.length > 0 && (
              <div className="cancel-policy-cp">
                <label>
                  <p className="mb-0">
                    <strong>Cancellation Policy: </strong>
                  </p>
                  {isNonRefundableCheckout ? (
                    nrCheckoutDisplayLines.map((line, idx) => (
                      <p key={idx} className="mb-1 ps-2">
                        {line.replace(/#!#|#\^#/g, "")}
                      </p>
                    ))
                  ) : (
                    <p className="mb-0">
                      {checkoutCancellationText
                        ? checkoutCancellationText.replace(/#!#|#\^#/g, "")
                        : "—"}
                    </p>
                  )}
                </label>
              </div>
            )}
          </>
        </Col>
      </Row>
      <div className="hotel-details-box-wrapper cp-box-none">
        {Object.keys(hotelDetailsObj).length > 0 && (
          <div className="hotel-details-img-name">
            <div className="image-details">
              {imageList.length > 0 ? (
                <img
                  src={imageList[0].path}
                  alt={safeHotel.hotelName}
                />
              ) : (
                <img src={hotelNoImg} alt="no-photo" />
              )}
            </div>
            <div className="name-details">
              <p>
                {hotelDetailsObj.hotelName}
                <sup>
                </sup>
              </p>
            </div>
          </div>
        )}

        <div className="stay-details-container">
          <div className="staying-date">
            <label className="hotel-stay-name">Marina Bay Beach Resort</label>
            <p className="locaton-hotel-div">
              Behind Bobs inn,Anna Vaddo, Candolim, GOA,
            </p>

            {Object.keys(hotelSearchData).length > 0 ? (
              <span>
                {formatDate(hotelSearchData.checkInDate, "DD MMM YYYY")}
                {" - "}
                {formatDate(hotelSearchData.checkOutDate, "DD MMM YYYY")}
              </span>
            ) : (
              ""
            )}

            <span className="number-hotel-spin">Nights: {noOfNights()}</span>
          </div>
          <div className="staying-date hotel-stay-date-row">
            {Object.keys(hotelDetailsObj).length > 0 && (
              <label>
                {rooms.length > 0 ? (
                  <>
                    {getHotelRoomDisplayTitle(firstRoom, safeHotel?.hotelName)}
                    {firstRoom?.roomDesc &&
                    firstRoom.roomDesc !== firstRoom.roomName &&
                    firstRoom.roomDesc !== firstRoom?.rc &&
                    firstRoom.roomDesc !== getHotelRoomDisplayTitle(firstRoom, safeHotel?.hotelName)
                      ? ` — ${firstRoom.roomDesc}`
                      : ""}
                    {cancellationPolicies[0]?.policyName
                      ? ` — ${cancellationPolicies[0].policyName}`
                      : ""}
                  </>
                ) : (
                  ""
                )}
              </label>
            )}
            {roomMealCategory ? (
              <div className="room-meal-category">{roomMealCategory}</div>
            ) : null}
            {getAdultChildCount()}
          </div>
        </div>
      </div>

      <Modal
        wrapClassName="modalHeader hotelImgModal checkout-hotel-img-modal"
        open={imagesModal.visible}
        visible={imagesModal.visible}
        title={safeHotel.hotelName || "Hotel images"}
        onCancel={() => setImagesModal({ visible: false, startIndex: 0 })}
        footer={null}
        destroyOnClose
        centered
        width={920}
      >
        {imagesModal.visible ? (
          <ImagesLightbox
            hotelImages={hotelImages}
            selectedItem={Number(imagesModal.startIndex) || 0}
            centerMode={false}
          />
        ) : null}
      </Modal>
    </>
  );
};

export default HotelDetailsBox;
